import {
  CalendarIcon,
  ChevronUpIcon,
  ClipboardCheckIcon,
  ClockIcon,
  HomeIcon,
} from '@heroicons/react/outline'
import { useWindowScroll } from '@mantine/hooks'
import dayjs from 'dayjs'
import useKDCApply from 'features/onboarding/hooks/useKDCApply'
import { IKdcAttr } from 'features/onboarding/types'
import _ from 'lodash'
import { AdditionalList } from 'mocks/kdt'
import { FC, useMemo, useState } from 'react'
import TagManager from 'react-gtm-module'
import { sec2time } from 'utils/sec2time'

import { ICourse } from '../../stores/courses'
import KlassInfoHeader from './KlassInfoHeader'

dayjs.locale('ko')

export interface IApplyPeriodList {
  [status: string]: {
    [date: string]: string
  }
}

interface ICourseInfoProps {
  course: ICourse
  isMobile?: boolean
  curEpisode: IKdcAttr
  onCompleteDialogOpenHandler?: () => void
}

const CourseInfoKDC: FC<ICourseInfoProps> = ({
  course,
  isMobile,
  curEpisode,
  onCompleteDialogOpenHandler,
}) => {
  const { title, price, panelText, totalSeconds, id, shareUrl, imageM } = course
  const { onApply } = useKDCApply(id)
  const curPrice = price.toLocaleString()
  const [openAddition, setOpenAddition] = useState<string | null>(null)

  const handleItemClick = (itemId: string) => {
    setOpenAddition(itemId === openAddition ? null : itemId)
  }
  const [scroll] = useWindowScroll()

  const detailInfoList = useMemo(
    () => [
      {
        icon: <CalendarIcon className="h-5 w-5" />,
        title: '수강기간',
        content: `${dayjs(curEpisode?.startedAt).format('MM.DD')} ~ ${dayjs(
          curEpisode?.endedAt,
        ).format('MM.DD')} ${
          ['kdc-datadata'].includes(course.id) ? '(수료 시 1년연장)' : '(수료 시 평생소장)'
        }`,
      },
      {
        icon: <ClockIcon className="h-5 w-5" />,
        title: '강의분량',
        content: `${sec2time(totalSeconds ?? 0, 'H시간 mm분')}`,
      },
      {
        icon: <HomeIcon className="h-5 w-5" />,
        title: '수강방식',
        content: '온라인',
      },
      {
        icon: <ClipboardCheckIcon className="h-5 w-5" />,
        title: '모집기간',
        content: `${dayjs(curEpisode?.applyStartedAt).format('MM.DD')} ~ ${dayjs(
          curEpisode?.applyEndedAt,
        ).format('MM.DD a hh시')}`,
      },
    ],
    [course, curEpisode],
  )

  return (
    <>
      {course && (
        <>
          {isMobile ? (
            <>
              <div className="block border-b lg:hidden">
                <div className="mx-4 pb-6 pt-5">
                  <KlassInfoHeader
                    isMobile
                    klassId={id}
                    title={title}
                    panelText={panelText}
                    shareUrl={shareUrl}
                    imgUrl={imageM}
                  />
                  {panelText && (
                    <p className="mb-[18px] break-keep border-b border-gray-300 py-3">
                      <span className="text-sm text-gray-500">{panelText}</span>
                    </p>
                  )}
                  <ul className="flex flex-col space-y-3">
                    {detailInfoList.map(({ icon, title, content }, idx) => (
                      <li key={`${title}_${idx}`} className="flex items-center text-sm">
                        <span className="inline-flex items-center">
                          <span>{icon}</span>
                          <span className="ml-2 min-w-12.5 font-semibold">{title}</span>
                        </span>
                        <span className="ml-2 mr-1 whitespace-nowrap">{content}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* 부가 설명 */}
                <div className="mx-4 mb-6 flex flex-col gap-y-2 lg:mt-5">
                  {AdditionalList['kdc'].map(addition => {
                    const isOpen = openAddition === addition.id
                    return (
                      <section
                        className="cursor-pointer rounded-lg bg-gray-50 px-4 py-3"
                        key={addition.id}
                        onClick={() => handleItemClick(addition.id)}>
                        <div className="flex items-center justify-between gap-x-2">
                          <div className="flex items-center gap-x-2">
                            <div className="flex h-6 w-6 items-center justify-center">
                              <img src={addition.icon} alt={addition.title} />
                            </div>
                            <p className="text-sm font-bold">
                              {addition.boldTitle}
                              <span className="ml-1 inline-block font-normal">
                                {addition.title}
                              </span>
                            </p>
                          </div>
                          <ChevronUpIcon
                            className={`${isOpen ? '' : 'rotate-180 transform'} w-4 text-gray-400`}
                          />
                        </div>
                        {/* open */}
                        {isOpen && (
                          <div className="mt-4 flex flex-col gap-y-1.5">
                            {addition.summary && (
                              <p className="text-xs font-semibold">{addition.summary}</p>
                            )}
                            <p className="text-xs text-gray-500">
                              <span className="font-semibold">{addition.boldDesc}</span>
                              {addition.desc}
                            </p>
                            {addition.secondSummary && (
                              <p className="mt-2.5 text-xs font-semibold">
                                {addition.secondSummary}
                              </p>
                            )}
                            {addition.secondDesc && (
                              <p className="text-xs text-gray-500">{addition.secondDesc}</p>
                            )}
                            {addition.reference && (
                              <span className="text-xs text-gray-500">{addition.reference}</span>
                            )}
                          </div>
                        )}
                      </section>
                    )
                  })}
                </div>
              </div>
              <div
                className="fixed bottom-0 left-0 z-30 block w-full bg-white shadow-[0px_-1px_4px_0px_rgba(0,0,0,0.05)] lg:hidden"
                style={{ minWidth: 320 }}>
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="shrink-0">
                    <p className="text-sm">
                      <span className="font-bold text-red-450">90%</span>
                      <span className="pl-1 text-gray-400 line-through">{curPrice}원</span>
                    </p>
                    <p>
                      <span className="text-lg font-semibold">
                        {(Math.round((price * 0.1) / 10) * 10).toLocaleString()}원
                      </span>
                      <span className="ml-[6px] inline-block rounded bg-red-100 px-[6px] py-[2px] text-xs font-semibold text-red-450">
                        {['kdc-datadata'].includes(course.id)
                          ? '수료 시 1년연장'
                          : '수료 시 평생소장'}
                      </span>
                    </p>
                  </div>
                  <button
                    disabled={_.isEmpty(curEpisode)}
                    onClick={() => {
                      void onApply(id, onCompleteDialogOpenHandler)
                      TagManager.dataLayer({
                        dataLayer: {
                          event: 'kdc_apply_btn_click',
                          content_ids: id,
                          ...(sessionStorage.getItem('utmAndGclid') &&
                            JSON.parse(sessionStorage.getItem('utmAndGclid') as string)),
                        },
                      })
                    }}
                    className={`ml-4 w-full rounded-md px-6 py-[10px] ${
                      _.isEmpty(curEpisode) ? 'cursor-not-allowed bg-gray-300' : 'bg-primary-normal'
                    }`}>
                    <span className="text-base font-semibold text-white md:text-lg">
                      {_.isEmpty(curEpisode) ? '수강 마감' : '수강 신청하기'}
                    </span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="px-4 pt-5 lg:rounded-lg lg:border lg:border-gray-300 lg:p-6">
                <KlassInfoHeader
                  isMobile={false}
                  klassId={id}
                  title={title}
                  panelText={panelText}
                  shareUrl={shareUrl}
                  imgUrl={imageM}
                />
                {panelText && (
                  <p className="mb-[18px] break-keep border-b border-gray-300 py-3">
                    <span className="text-sm text-gray-500">{panelText}</span>
                  </p>
                )}
                <ul className="flex flex-col space-y-3">
                  {detailInfoList.map(({ icon, title, content }, idx) => (
                    <li key={`${title}_${idx}`} className="flex items-center text-sm">
                      <span className="inline-flex items-center">
                        <span>{icon}</span>
                        <span className="ml-2 min-w-12.5 font-semibold">{title}</span>
                      </span>
                      <span className="ml-2 mr-1 whitespace-nowrap">{content}</span>
                    </li>
                  ))}
                </ul>
                <hr className="my-4 border-gray-300" />
                <p className="flex text-sm">
                  <span className="font-bold text-red-450">90%</span>
                  <span className="pl-1 text-gray-400 line-through">{curPrice}원</span>
                </p>
                <p className="mt-1">
                  <span className="text-xl font-bold">
                    {(Math.round((price * 0.1) / 10) * 10).toLocaleString()}원
                  </span>
                  <span className="ml-2 inline-block rounded bg-red-100 px-[6px] py-[2px] text-sm font-semibold text-red-450">
                    {['kdc-datadata'].includes(course.id) ? '수료 시 1년연장' : '수료 시 평생소장'}
                  </span>
                </p>
                <button
                  disabled={_.isEmpty(curEpisode)}
                  onClick={() => {
                    void onApply(id, onCompleteDialogOpenHandler)
                    TagManager.dataLayer({
                      dataLayer: {
                        event: 'kdc_apply_btn_click',
                        content_ids: id,
                        ...(sessionStorage.getItem('utmAndGclid') &&
                          JSON.parse(sessionStorage.getItem('utmAndGclid') as string)),
                      },
                    })
                  }}
                  className={`mt-[18px] w-full rounded-md p-2 ${
                    _.isEmpty(curEpisode) ? 'cursor-not-allowed bg-gray-300' : 'bg-primary-normal'
                  }`}>
                  <span className="text-base font-semibold text-white md:text-lg">
                    {_.isEmpty(curEpisode) ? '수강 마감' : '수강 신청하기'}
                  </span>
                </button>
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}

export default CourseInfoKDC
